import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faFlask, faRocket, faCopy, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import config from '../../configs/config';
import { useToast } from '../../contexts/ToastContext';
import './styles/LinkModal.css';

// Single combined modal for both survey actions that used to live under the
// old "PSNST Survey" Preview-dropdown option:
//   - Test: opens the real survey experience but writes into the isolated
//     "TestData" collection, never real responses, and always renders
//     whatever's currently Saved (sqgroup_json) - even mid-edit. Always
//     available, just Copy/Open - no raw link shown.
//   - Publish: copies the current sqgroup_json into psnst_json - the field
//     real respondents' survey actually renders from (see PollStudioNST's
//     fetch_survey_row) - and records an audit entry (publisher + link +
//     timestamp). Repeatable: publishing again re-syncs psnst_json with
//     whatever's been Saved since, so a post-launch fix can go live without
//     needing a new project. There's no "un-publish".
const SurveyLinkModal = ({ isOpen, onClose, pid, projectId, publisherEmail, publisherName }) => {
  const { toast } = useToast();
  const [copiedTest, setCopiedTest] = useState(false);
  const [copiedLive, setCopiedLive] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [history, setHistory] = useState([]);

  const angularBaseUrl = process.env.REACT_APP_SURVEY_PREVIEW_URL || 'http://localhost:8100';
  // A distinct path (not a ?mode=test query param) so a respondent editing
  // or cleaning up the URL in their address bar can't accidentally flip a
  // test session into writing real data - see app-routing.module.ts.
  const testLink = useMemo(() => (pid ? `${angularBaseUrl}/survey-test/${pid}` : ''), [pid, angularBaseUrl]);
  const liveLink = useMemo(() => (pid ? `${angularBaseUrl}/survey/${pid}` : ''), [pid, angularBaseUrl]);

  useEffect(() => {
    if (!isOpen) return;
    setCopiedTest(false);
    setCopiedLive(false);
  }, [isOpen]);

  // Pulled out of the effect below so handlePublish's catch block can also
  // call it directly - reconciling actual server state after a request that
  // errored out client-side (dropped connection, timeout) but may well have
  // already committed server-side, so the popup never gets stuck showing
  // "Publish" for a survey that's actually already live.
  const loadHistory = async () => {
    if (!projectId) return;
    try {
      const res = await fetch(`${config.API_BASE_URL}/get-publish-audit/${projectId}`, {
        credentials: 'include',
      });
      const result = await res.json();
      if (result.success) setHistory(result.history || []);
    } catch (err) {
      console.error('Failed to load publish history:', err);
    }
  };

  useEffect(() => {
    if (!isOpen || !projectId) return;
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, projectId]);

  if (!isOpen) return null;

  // Whether this survey has ever been published, in this session or a
  // prior one (per the audit history) - just controls whether we show
  // "Publish" or "Re-publish" and whether the live-link section is shown
  // yet. Publishing again is always allowed - see handlePublish.
  const everPublished = history.length > 0;

  const copyLink = (link, setCopied) => {
    if (!link) return;
    navigator.clipboard
      .writeText(link)
      .then(() => {
        setCopied(true);
        toast.success('Link copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => toast.error('Failed to copy link.'));
  };

  const openLink = (link) => {
    if (link) window.open(link, '_blank');
  };

  const handlePublish = async () => {
    if (!projectId || !liveLink) return;
    const confirmMsg = everPublished
      ? 'Re-publish this survey? This overwrites the LIVE survey with whatever is currently Saved - respondents will immediately see the updated version.'
      : 'Publish this survey live? Respondents will be able to submit real answers, using whatever is currently Saved.';
    if (!window.confirm(confirmMsg)) return;

    setPublishing(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/save-publish-audit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ projectId, link: liveLink, publisherEmail, publisherName }),
      });
      const result = await res.json();
      if (result.success) {
        // Prepend locally so "Last published by..." reflects this publish
        // immediately, without waiting on a re-fetch.
        setHistory((prev) => [result.entry, ...prev]);
        toast.success(everPublished ? 'Survey re-published!' : 'Survey published!');
      } else {
        toast.error('Failed to publish: ' + (result.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error publishing survey:', err);
      toast.error('Error publishing survey - checking if it went through...');
      // The request may have failed after the server already committed the
      // publish (dropped connection, timeout, etc.) - reconcile with the
      // actual server state instead of leaving the popup stuck on "Publish".
      await loadHistory();
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="linkmodal-overlay" onClick={onClose}>
      <div className="linkmodal-content" onClick={(e) => e.stopPropagation()}>
        <div className="linkmodal-header">
          <h3>
            <FontAwesomeIcon icon={faRocket} style={{ marginRight: '8px' }} />
            Survey Links
          </h3>
          <button className="linkmodal-close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="linkmodal-body">
          {!pid ? (
            <p className="linkmodal-warning">PID not available. Please save the survey first.</p>
          ) : (
            <>
              <div className="linkmodal-section">
                <h4 className="linkmodal-section-title">
                  <FontAwesomeIcon icon={faFlask} /> Test
                </h4>
                <div className="linkmodal-section-actions">
                  <button className="linkmodal-copy-btn" onClick={() => copyLink(testLink, setCopiedTest)}>
                    <FontAwesomeIcon icon={faCopy} /> {copiedTest ? 'Copied!' : 'Copy Test Link'}
                  </button>
                  <button className="linkmodal-open-btn" onClick={() => openLink(testLink)}>
                    <FontAwesomeIcon icon={faExternalLinkAlt} /> Open Test Link
                  </button>
                </div>
              </div>

              <div className="linkmodal-section">
                <h4 className="linkmodal-section-title">
                  <FontAwesomeIcon icon={faRocket} /> Publish
                </h4>

                <p className="linkmodal-confirm">
                  {everPublished
                    ? 'Overwrites what respondents see right now with your current Saved draft.'
                    : 'Writes real respondent data to production, using your current Saved draft.'}
                </p>

                <div className="linkmodal-section-actions">
                  {everPublished && (
                    <>
                      <button className="linkmodal-copy-btn" onClick={() => copyLink(liveLink, setCopiedLive)}>
                        <FontAwesomeIcon icon={faCopy} /> {copiedLive ? 'Copied!' : 'Copy Live Link'}
                      </button>
                      <button className="linkmodal-open-btn" onClick={() => openLink(liveLink)}>
                        <FontAwesomeIcon icon={faExternalLinkAlt} /> Open Live Link
                      </button>
                    </>
                  )}
                  <button className="linkmodal-publish-btn" onClick={handlePublish} disabled={publishing}>
                    <FontAwesomeIcon icon={faRocket} /> {publishing ? 'Publishing...' : everPublished ? 'Re-publish' : 'Publish'}
                  </button>
                </div>

                {everPublished && (
                  <>
                    <div className="linkmodal-history">
                      Last published by {history[0].publisherEmail || history[0].publisherName || 'unknown'} on{' '}
                      {history[0].publishedAt ? new Date(history[0].publishedAt).toLocaleString() : 'unknown date'}
                    </div>
                    <p className="linkmodal-hint" style={{ marginTop: '12px' }}>
                      Sending to a sample/panel list? Append <strong>?uid=</strong> with your own ID per
                      recipient to give each one a distinct, resumable response - e.g.:
                      <br />
                      <code style={{ wordBreak: 'break-all' }}>{liveLink}?uid=<strong>respondent123</strong></code>
                    </p>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        <div className="linkmodal-footer">
          <button className="linkmodal-cancel-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default SurveyLinkModal;
